<template>
  <v-modal
    v-if="opened"
    title="New Template"
    :actions="getActions"
    classType="primary"
    version="new"
    extraBackdropClass="new-template-modal__wrapper"
  >
    <VSwitcher
      :activeKey="dataKey"
      :switcherKeys="switcherKeys"
      type="element"
      @change="_updateLayout"
      extraClass="new-template-modal"
    ></VSwitcher>

    <WithLoader :isLoading="isLoading" classType="primary">
      <div class="templates-new-form__wrapper">
        <TemplatesCreateForm
          :templateType="dataKey"
          @templatetype="val => setPostcardType(val)"
          @submit="addTemplate"
        ></TemplatesCreateForm>
      </div>
    </WithLoader>
  </v-modal>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";

// components
import VModal from "@/components/VModal";
import TemplatesCreateForm from "@/components/TemplatesCreateForm";
import VSwitcher from "@/components/VSwitcher";

// mixins
import withFormUtils from "@/mixins/withFormUtils";
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";

import postcardIcon from "@/assets/images/postcard-icon-light.svg";
import postcardIconActive from "@/assets/images/postcard-icon-active.svg";
import letterIcon from "@/assets/images/letter-icon.svg";
import letterIconActive from "@/assets/images/letter-icon-active.svg";

import { getDefaultTemplateOpts } from "@/constants/forms";

export default Vue.component("TemplatesNewOneModal", {
  props: {
    opened: VueTypes.bool.isRequired
  },

  mixins: [withFormUtils, withToasted, withNavigate],

  data() {
    const { current } = this.$router.history;
    const dataKey = current.name === "templates" ? "postcard" : "letter";

    return {
      dataKey,
      postcardType: "postcard-4x6",
      name: "createTemplate"
    };
  },

  components: {
    VModal,
    TemplatesCreateForm,
    VSwitcher
  },

  methods: {
    _updateLayout(active) {
      this.dataKey = active;
    },

    _handleSuccessSave(templateName, redirectUrl) {
      this.toast(`Template ${templateName} has been created successfully!`);
      this.$emit("close");
      this.navigate(redirectUrl);
    },

    _handleErrorCase(e) {
      this.showServerErrors(e);
    },

    addTemplate() {
      this.validateForm();

      if (this.isValid) {
        let data = {
          ...this.formValues,
          ...getDefaultTemplateOpts(this.dataKey)
        };

        if (this.dataKey === "postcard") {
          data.type = this.postcardType;
          data.data_type = "html";

          this.createTemplate({
            data,
            onSuccess: template => {
              this._handleSuccessSave(
                template.name,
                `/postcards-templates/settings/${template.id}?type=postcard`
              );
            },
            onError: this._handleErrorCase
          });
        } else {
          this.createLetterTemplate({
            data,
            onSuccess: template => {
              this._handleSuccessSave(
                template.name,
                `/letters-templates/settings/${template.id}?type=letter`
              );
            },
            onError: this._handleErrorCase
          });
        }
      }
    },

    setPostcardType(type) {
      this.postcardType = type;
    },

    ...mapActions("templates", ["createTemplate"]),
    ...mapActions("lettersTemplates", ["createLetterTemplate"])
  },

  computed: {
    getActions() {
      return [
        {
          label: "Cancel",
          value: "cancel",
          type: "primary",
          size: "wide",
          noBorder: true,
          inverted: true,
          click: () => this.$emit("close")
        },
        {
          label: "Continue",
          value: "add",
          type: "primary",
          size: "regular",
          click: this.addTemplate,
          disabled: !this.isValid || this.isLoading
        }
      ];
    },

    switcherKeys() {
      return [
        {
          key: "postcard",
          label: "Postcard",
          icon: postcardIcon,
          iconActive: postcardIconActive
        },

        {
          key: "letter",
          label: "Letter",
          icon: letterIcon,
          iconActive: letterIconActive
        }
      ];
    },

    isLoading() {
      return this.isLoadingPostcardsTemplates || this.isLoadingLettersTemplates;
    },

    ...mapGetters("templates", { isLoadingPostcardsTemplates: "isLoading" }),
    ...mapGetters("lettersTemplates", {
      isLoadingLettersTemplates: "isLoading"
    })
  }
});
</script>

<style lang="scss" scoped>
.new-template-modal {
  margin-bottom: 30px;

  &__wrapper /deep/ .v-modal__container {
    min-height: 565px;
  }
}
</style>
